import { useCallback, useMemo } from 'react';

import { MedicalLeavePayerFromPueGetCommand, MedicalLeavePuePayer, PueFault } from '@dreryk/egabinet-ezla-frontend-api';
import { InsuranceType } from '@dreryk/egabinet-ezla-published-language-api';
import { IdentityDocument, PatientExternalIdentifier } from '@dreryk/egabinet-patient-published-language-api';
import { NoValidCertificateFoundFault } from '@dreryk/egabinet-truststore-published-language-api';

import { useCqrsCommand } from '@dreryk/command-query-react-common';
import { dispatchSnackbarNegativeEvent } from '@dreryk/react-components';
import { Slot } from '@dreryk/watermelon';

import { CertificateFault, useCertificateFaultHandlerMap } from '~libs.desktop.shared.account';

import { commonTranslationKeys } from '../../../../../translations';

type UsePuePayersReturn = [() => () => Promise<MedicalLeavePuePayer[]>, MedicalLeavePuePayer[] | undefined];
export const usePuePayers = (
   patientIdentityDocument: IdentityDocument | undefined,
   patientExternalIdentifier: PatientExternalIdentifier | undefined,
   insuranceType: InsuranceType,
   onAuthenticationRequiredFault: () => void,
   onPueFault: (fault: PueFault) => void
): UsePuePayersReturn => {
   const { executeAsync: fetchPayers, data: payers } = useCqrsCommand<
      MedicalLeavePuePayer[],
      void,
      CertificateFault | NoValidCertificateFoundFault | PueFault
   >(
      () => {
         const command = new MedicalLeavePayerFromPueGetCommand();
         command.patientExternalIdentifier = patientExternalIdentifier!;
         command.patientIdentityDocument = Slot.emptyIfNull(patientIdentityDocument ?? null);
         command.insuranceType = insuranceType;
         return command;
      },
      {
         onFault: {
            ...useCertificateFaultHandlerMap({
               onAuthenticationRequired: onAuthenticationRequiredFault,
            }),
            'egabinet-truststore:NoValidCertificateFoundFault': () => {
               dispatchSnackbarNegativeEvent(commonTranslationKeys.missingDoctorActiveP1CertificateFault);
            },
            'egabinet-ezla-frontend-api:PueFault': (fault) => {
               dispatchSnackbarNegativeEvent(commonTranslationKeys.submitError);
               onPueFault(fault);
            },
         },
      }
   );

   /*
    * useDeepCompareEffect(() => {
    *    if (patientExternalIdentifier && !patientLoading) {
    *       fetchPayers();
    *    }
    * }, [fetchPayers, patientExternalIdentifier, insuranceType, patientLoading, patient?.identityDocument]);
    */

   const payersSearchCreator = useCallback(() => fetchPayers, [fetchPayers]);

   return useMemo(() => [payersSearchCreator, payers], [payersSearchCreator, payers]);
};
